import api from "../api/axios";
import {
  uploadEventPromoDrafts,
  uploadPreparedMediaFileWithProgress,
} from "../pages/events/eventGalleryUpload";

const DB_NAME = "vaultgrid-media-uploads";
const DB_VERSION = 1;
const STORE_NAME = "event-promo-uploads";
const MAX_WORKERS = 2;
const jobs = new Map();
const listeners = new Set();
let activeUserId = "";
let activeWorkers = 0;
let initialized = false;

function newUploadId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function openDatabase() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function persistJob(job) {
  try {
    const db = await openDatabase();
    if (!db) return false;
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put({
        id: job.id,
        clientUploadId: job.clientUploadId,
        userId: job.userId,
        eventId: job.eventId,
        eventName: job.eventName,
        name: job.name,
        type: job.type,
        file: job.file,
        posterFile: job.posterFile || null,
        status: job.status === "uploading" ? "queued" : job.status,
        attempts: job.attempts || 0,
        total: job.total,
        createdAt: job.createdAt,
      });
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
    db.close();
    return true;
  } catch (error) {
    console.warn("Could not persist event promo upload", error);
    return false;
  }
}

async function deletePersistedJob(id) {
  try {
    const db = await openDatabase();
    if (!db) return;
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).delete(id);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });
    db.close();
  } catch (error) {
    console.warn("Could not clear completed event promo upload", error);
  }
}

async function loadPersistedJobs() {
  try {
    const db = await openDatabase();
    if (!db) return [];
    const stored = await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return stored;
  } catch (error) {
    console.warn("Could not restore event promo uploads", error);
    return [];
  }
}

function getSnapshot() {
  const visible = [...jobs.values()].filter((job) => !activeUserId || job.userId === activeUserId);
  const totalBytes = visible.reduce((sum, job) => sum + Number(job.total || 0), 0);
  const loadedBytes = visible.reduce((sum, job) => {
    if (job.status === "complete") return sum + Number(job.total || 0);
    return sum + Number(job.loaded || 0);
  }, 0);
  return {
    jobs: visible.map(({ file, posterFile, posterPromise, previewUrl, ...job }) => job),
    total: visible.length,
    uploading: visible.filter((job) => ["persisting", "retrying", "uploading", "queued"].includes(job.status)).length,
    failed: visible.filter((job) => job.status === "failed").length,
    completed: visible.filter((job) => job.status === "complete").length,
    percent: totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0,
  };
}

function emit() {
  const snapshot = getSnapshot();
  listeners.forEach((listener) => listener(snapshot));
}

async function processJob(job) {
  job.status = "uploading";
  job.loaded = 0;
  job.error = "";
  emit();

  try {
    if (!job.posterFile && job.posterPromise) {
      const poster = await job.posterPromise.catch(() => null);
      if (poster?.posterFile) {
        job.posterFile = poster.posterFile;
        await persistJob(job);
      }
    }
    if (String(job.previewUrl || "").startsWith("blob:")) {
      URL.revokeObjectURL(job.previewUrl);
      job.previewUrl = "";
    }
    const result = await uploadEventPromoDrafts({
      apiClient: api,
      eventId: job.eventId,
      drafts: [{
        name: job.name,
        file: job.file,
        type: job.type,
        posterFile: job.posterFile,
        clientUploadId: job.clientUploadId,
      }],
      uploadFile: uploadPreparedMediaFileWithProgress,
      concurrency: 1,
      cleanupFailedPrepared: false,
      onFileProgress: (_index, loaded, total) => {
        job.loaded = Math.min(Number(loaded || 0), Number(total || job.total || 0));
        emit();
      },
    });
    if (result.failedDrafts.length) throw result.failures[0]?.error || new Error("Upload failed.");

    job.status = "complete";
    job.loaded = job.total;
    job.uploadedItems = result.uploadedItems;
    emit();
    await deletePersistedJob(job.id);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("vg-event-promo-uploaded", {
        detail: { eventId: job.eventId, items: result.uploadedItems },
      }));
      window.setTimeout(() => {
        jobs.delete(job.id);
        emit();
      }, 8000);
    }
  } catch (error) {
    job.attempts = Number(job.attempts || 0) + 1;
    job.status = "failed";
    job.loaded = 0;
    job.error = error?.response?.data?.detail || error?.message || "Upload failed.";
    await persistJob(job);
    emit();
    if (job.attempts < 3 && (typeof navigator === "undefined" || navigator.onLine !== false)) {
      job.status = "retrying";
      await persistJob(job);
      emit();
      window.setTimeout(() => {
        if (job.status !== "retrying") return;
        job.status = "queued";
        persistJob(job);
        emit();
        pump();
      }, job.attempts === 1 ? 2000 : 10000);
    }
  }
}

function launchJob(job) {
  activeWorkers += 1;
  processJob(job).finally(() => {
    activeWorkers -= 1;
    pump();
  });
}

function pump() {
  if (!activeUserId || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
  const availableSlots = Math.max(0, MAX_WORKERS - activeWorkers);
  const ready = [...jobs.values()]
    .filter((job) => job.userId === activeUserId && job.status === "queued")
    .slice(0, availableSlots);
  ready.forEach(launchJob);
}

export async function startEventPromoUploadQueue(userId) {
  activeUserId = String(userId || "");
  if (!activeUserId || initialized) {
    emit();
    pump();
    return;
  }
  initialized = true;
  if (typeof navigator !== "undefined" && navigator.storage?.persist) {
    navigator.storage.persist().catch(() => false);
  }
  const stored = await loadPersistedJobs();
  stored
    .filter((job) => job.userId && job.file)
    .forEach((job) => jobs.set(job.id, {
      ...job,
      userId: String(job.userId),
      status: job.status === "failed" && Number(job.attempts || 0) >= 3 ? "failed" : "queued",
      loaded: 0,
    }));
  emit();
  pump();
}

export async function enqueueEventPromoUploads({ eventId, eventName = "", drafts = [] }) {
  if (!eventId || !activeUserId || !drafts.length) return;
  const created = drafts.map((draft) => {
    const id = newUploadId();
    const job = {
      id,
      clientUploadId: id.replace(/[^A-Za-z0-9_-]/g, "_"),
      userId: activeUserId,
      eventId: Number(eventId),
      eventName: String(eventName || ""),
      name: draft.name || draft.file?.name || "event-media",
      type: draft.type,
      file: draft.file,
      posterFile: draft.posterFile || null,
      posterPromise: draft.posterPromise || null,
      previewUrl: draft.previewUrl || "",
      status: "persisting",
      attempts: 0,
      loaded: 0,
      total: Number(draft.file?.size || 0),
      createdAt: Date.now(),
    };
    jobs.set(id, job);
    return job;
  });
  emit();
  await Promise.all(created.map(async (job) => {
    await persistJob(job);
    job.status = "queued";
    emit();
    pump();
  }));
}

export function retryEventPromoUploads() {
  jobs.forEach((job) => {
    if (job.userId === activeUserId && job.status === "failed") {
      job.status = "queued";
      job.attempts = 0;
      persistJob(job);
    }
  });
  emit();
  pump();
}

export function subscribeEventPromoUploadQueue(listener) {
  listeners.add(listener);
  listener(getSnapshot());
  return () => listeners.delete(listener);
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    jobs.forEach((job) => {
      if (job.userId === activeUserId && job.status === "failed" && job.attempts < 3) job.status = "queued";
    });
    emit();
    pump();
  });
}
